import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { JSMABridge } from '@indeed/jsma-js-bridge';
import { Box, Button, Heading, Spinner, Text } from '@indeed/ifl-components';
import { Discover, Edit, Jobs } from '@indeed/ifl-icons';
import { FREE_TEXT_PREFERENCE_EDIT_PATH } from '../free-text-preference/FreeTextPreferenceEditPage';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';
import { useInitialState } from '../../../lib/context/InitialStateContext';
import { ErrorDialog } from '../../../components/ErrorDialog/ErrorDialog';
import GettextFactory from '../../../util/GettextFactory';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { AutoApplySetupRumContext, logAutoApplySetupRumError } from '../autoApplySetupRumErrors';
import { AutoApplySetupAction } from '../autoApplySetupActions';
import { useAutoApplyFreeTextPreferencesQuery } from './__generated__/FreeTextPreferencesSection.generated';

const formatLastModified = (
    timestamp: number | null | undefined,
    locale: string,
): string | null => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    // The wire format of the Timestamp scalar is unverified; never let a bad value crash the section.
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(date);
};

/**
 * FreeTextPreferencesSection Component
 * Displays the job seeker's free-text match preference ("blurb") with an edit button.
 *
 * Figma: https://www.figma.com/design/dWz1Ao3NwPCSdhrsiG6Yvq/Review-before-submit-2026?node-id=899-48627
 *
 * Replaces MatchPreferencesSection in CustomizeExperienceReview when the
 * `apply_for_me_free_text_preference_tst` proctor is in the test group.
 * Self-contained: fetches `autoApplyInfo.freeTextPreference` itself.
 *
 * JIRA: IAFE-3512
 */
export interface FreeTextPreferencesSectionProps {
    /** Disables the edit button while preferences are saving or the page is leaving. */
    editDisabled?: boolean;
}

export const FreeTextPreferencesSection: React.FC<FreeTextPreferencesSectionProps> = ({
    editDisabled,
}) => {
    const { locale } = useInitialState();
    const location = useLocation();
    const { enterSubFlow } = usePageFlow();
    const gt = GettextFactory();
    const eventLogger = useEventLogger();
    const [showErrorDialog, setShowErrorDialog] = useState(false);

    const { data, loading, error, refetch } = useAutoApplyFreeTextPreferencesQuery({
        fetchPolicy: 'cache-first',
    });

    useEffect(() => {
        if (error) {
            logAutoApplySetupRumError(
                error,
                AutoApplySetupRumContext.FreeTextPreferencesLoad,
                false,
            );
            setShowErrorDialog(true);
        }
    }, [error]);

    const freeTextPreference = data?.autoApplyInfo?.freeTextPreference;
    const lastModified = formatLastModified(freeTextPreference?.lastModifiedTimestamp, locale);
    const sectionTitle = gt.gettext('Match preferences');

    let footnote: string | null = null;
    if (freeTextPreference?.modifiedByJobSeeker) {
        footnote = lastModified ? gt.gettext('Edited: {0}', [lastModified]) : null;
    } else if (freeTextPreference?.text) {
        footnote = gt.gettext('Based on your profile and activity on Indeed');
    }

    // Opens the edit page (IAFE-3513): a native webview modal in the app, or an
    // in-app navigation on web. The modal reports 'updated' after a successful save,
    // so the section refetches to show the new text.
    const handleEditClick = async () => {
        eventLogger.logAutoApplySetupButtonClickEvent?.({
            currentPage: location.pathname,
            button: AutoApplySetupAction.EditFreeTextPreferences,
        });
        const editPath = `${FREE_TEXT_PREFERENCE_EDIT_PATH}${location.search}`;
        // Opens the page as a sub-flow detour, which records the origin so the page
        // goes back instead of dismissing a native modal (it may still be inside the
        // native webview). enterSubFlow keeps the current query string.
        const openInApp = () => {
            void enterSubFlow(FREE_TEXT_PREFERENCE_EDIT_PATH);
        };
        if (!JSMABridge.isAvailable()) {
            openInApp();
            return;
        }
        try {
            const result = await JSMABridge.showModalWithWebViewAndWaitForResult({
                // Absolute path: the native webview doesn't resolve it against the current origin
                url: new URL(editPath, window.location.href).href,
                title: sectionTitle,
                presentationMode: 'FULLSCREEN',
            });
            if (result === 'updated') {
                void refetch();
            }
        } catch (bridgeError) {
            // The bridge call rejected (e.g. an older app build without the command);
            // fall back to the web page so the edit still works. Not a page failure.
            logAutoApplySetupRumError(
                bridgeError,
                AutoApplySetupRumContext.FreeTextPreferencesEdit,
                false,
            );
            openInApp();
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ flexShrink: 0 }}>
                        <Jobs size="md" aria-hidden />
                    </Box>
                    <Heading level={3} as="h2" sx={{ marginBlockEnd: 0 }}>
                        {sectionTitle}
                    </Heading>
                </Box>
                <Button
                    variant="tertiary"
                    aria-label={gt.gettext('Edit {0}', [sectionTitle])}
                    iconOnly
                    disabled={editDisabled || loading || !!error}
                    onClick={() => void handleEditClick()}
                >
                    <Edit size="md" variant="stroke" aria-hidden="true" />
                </Button>
            </Box>

            <ErrorDialog
                isOpen={showErrorDialog}
                onOpenChange={setShowErrorDialog}
                onAction={() => refetch()}
            />

            <Box
                sx={{
                    border: '1px solid',
                    borderColor: 'border.weak',
                    borderRadius: 'lg',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {loading || error ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                        {loading && <Spinner title={gt.gettext('Loading preferences')} />}
                    </Box>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 4 }}>
                            <Text level={3} sx={{ whiteSpace: 'pre-line' }}>
                                {freeTextPreference?.text || gt.gettext('Not added')}
                            </Text>
                            {footnote && (
                                <Text level={1} variant="secondary">
                                    {footnote}
                                </Text>
                            )}
                        </Box>
                        {!freeTextPreference?.modifiedByJobSeeker && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                    padding: 3,
                                    backgroundColor: 'expressive.blue.weak',
                                }}
                            >
                                <Box sx={{ display: 'flex', flexShrink: 0, paddingBlock: '2px' }}>
                                    <Discover size="sm" aria-hidden />
                                </Box>
                                <Text level={2}>
                                    {gt.gettext(
                                        'Cover what you’re looking for in your next job like role or pay, and anything you want to avoid.',
                                    )}
                                </Text>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};
